import {IStop} from "../interfaces/IStop";
import {IDirection} from "../interfaces/IDirection";
import {IPoint} from "../interfaces/IPoint";
import {IDanger} from "../interfaces/IDanger";
import {ITunnel} from "../interfaces/ITunnel";
import {Stop} from "../entities/Stop";
import {Encoder} from "../Tools/Encoder";
import {IBytecode} from "../interfaces/IBytecode";
import {LoopsAnalyzer} from "./LoopsAnalyzer";
import {LegBuilder} from "./LegBuilder";
import {Direction} from "../entities/Direction";
import {ILegBuilderDirectionProjection} from "../interfaces/ILegBuilderDirectionProjection";
import {IDirectionResolution} from "../interfaces/IDirectionResolution";
import {IUncertaintyResolution} from "../interfaces/IUncertaintyResolution";


const MAX_STOP_TRACK_GAP = 30; //The maximum allowed distance between a stop and it's interpolation on a leg.
const MAX_DIRECTION_GAP = 1; //The maximum allowed distance between a direction and it's interpolation on a leg.
//@ts-ignore
const PATH_BREAK_MIN_STD = 20; //This is the minimum standard deviation of two heading to say we're making a turn.

export class ByteCodeBuilder {

    stops: IStop[];
    directions: IDirection[];
    dangers: IDanger[];
    tunnels: ITunnel[];
    shapePoints: IPoint[];

    preComputedStopUncertaintyResolutions: IUncertaintyResolution[] = [];
    precomputedDirectionUncertaintyResolutions: IUncertaintyResolution[] = [];
    validSUR: IUncertaintyResolution[] = [];
    validDUR: IUncertaintyResolution[] = [];

    constructor() {
        this.shapePoints = [];
        this.stops = [];
        this.directions = [];
        this.dangers = [];
        this.tunnels = [];
        this.tunnels = [];
    }


    /**
     * Set the stop list for this builder.
     * @param stopList
     */
    public setStopList(stopList: IStop[]) : this {
       for(let iStop of stopList) {
           this.stops.push(new Stop(iStop.id, iStop.point, iStop.sequence));
       }
       return this;
    }

    setDirectionList(directionList: IDirection[]) : this {
        for(let iDirection of directionList) {
            this.directions.push(new Direction(iDirection.id, iDirection.point));
        }
        return this;
    }

    /**
     * Main shape, 1E-6 encoded.
     * @param encodedPolyline
     */
    setShape(encodedPolyline: string) : this {
        this.shapePoints = Encoder.decodePolyline(encodedPolyline);

        return this;
    }


    build() : IBytecode {
        if(this.shapePoints === null || this.shapePoints.length < 2) {
            throw new Error("Trying to build a map engine bytecode without enough points on the shape (" + this.shapePoints.length + ") Have you initiated them using builder.setShape() with a 1E-6 encoded polyline ?" );
        }
        let loops = (new LoopsAnalyzer(this.shapePoints)).analyse();
        for(let i = 1; i < loops.length; i++) {
            //For all loops, we will add the last point of the previous loop to have full coverage (except first one).
            loops[i].unshift(loops[i-1][loops[i-1].length -1]);
        }
        let legBuilders = loops.map((_,k) => new LegBuilder(this.shapePoints, loops, k));   //Leg builder !



        if(this.stops.length) {
            this.positionStops(this.stops, legBuilders);
        }
        //@ts-ignore
        let directionsResolution: IDirectionResolution|null = null; //Not used for now one.
        if(this.directions.length) {
            directionsResolution = this.positionDirections(this.directions, legBuilders);
        }

        /**
         * Behavior for direction, danger zones, tunnels : Since they are all made out by hubup,
         * they are mapped on the correct leg from interface, and inserted within the bytecode builder before
         * generating it. We will not check if they are correctly inserted and trust interface input, which MUST
         * ask a user decision.
         */


        //@todo: split legs according to geometry. ==> DONE

        //@todo : Handling directions assignment to leg => DONE

        //@todo : Handling computing dfo for all.  ==> DONE








        //@todo : Handling tunnel zones for all.   ==> NOT YET IMPLEMENTED

        //@todo : Handling tunnel zones for all.  ===> NOT YET IMPLEMENTED





        /**
         * Validating pre-computed stop uncertainty resolutions (sUR)
         */
        for(let sur of this.preComputedStopUncertaintyResolutions) {
            let containingStop: LegBuilder[] = legBuilders.filter(e => e.hasStop(sur.element_id));
            let matching: any = containingStop.find(e => {
                let p = e.getStopProjection(sur.element_id)?.projection;
                return p?.refLon === sur.refLon && p.refLat === sur.refLat;
            });
            if(matching != undefined) {
                legBuilders.map((e,k) => k === matching.legIndex ? e.updateStopCertainty(sur.element_id, false) : e.removeStopProjectionFromLeg(sur.element_id));
                this.validSUR.push(sur);
                console.log("Precomputed stop uncertainty was rematched successfully.");
            } else {
                console.log("Precomputed stop uncertainty was not rematchable.", sur);
            }

        }



        /**
         * Validating pre-computed stop uncertainty resolutions (sUR)
         */
        for(let dUR of this.precomputedDirectionUncertaintyResolutions) {
            let containingDirections: LegBuilder[] = legBuilders.filter(e => e.hasDirection(dUR.element_id));
            let matching: any = containingDirections.find(e => {
                let p = e.getDirectionProjection(dUR.element_id)?.projection;
                return p?.refLon === dUR.refLon && p.refLat === dUR.refLat;
            });

            if(matching != undefined) {
                legBuilders.map((e,k) => k === matching.legIndex ? e.updateDirectionCertainty(dUR.element_id, false) : e.removeDirectionProjectionFromLeg(dUR.element_id));
                this.validDUR.push(dUR);
            }
        }
        return {
            legs: legBuilders.map(e => e.build()),
            stopUncertaintyResolutions:  this.validSUR,
            directionUncertaintyResolutions:  this.validDUR
        };


    }


    /**
     *
     * @param directions
     * @param legBuilders
     */
    positionDirections(directions: IDirection[], legBuilders: LegBuilder[]) : IDirectionResolution {
        let unMatchableDirections = [];
        let smartResolvedDirections:any[] = [];
        let unResolvableDirections = [];
        let fullyResolvableDirections = [];
        for(let direction of directions) {
            let projections: ILegBuilderDirectionProjection[] = [];
            for(let legBuilder of legBuilders) {
                projections.push(legBuilder.projectDirection(direction));
            }
            projections = projections.filter(e => e.gap < MAX_DIRECTION_GAP);
            if(projections.length === 1) {
                //We have only one direction that matches what's required (perfect case).
                legBuilders[projections[0].legIndex].addDirectionProjectionToLeg(projections[0], false);
                fullyResolvableDirections.push(direction.id);
            } else if(projections.length === 0) {
                //There is no leg that comes close to that direction. We will NOT take it in account.
                unMatchableDirections.push(direction.id);
            } else {
                //Projection appears several times at valid places. Adding them as unsure.
                for(let p of projections) {
                    legBuilders[p.legIndex].addDirectionProjectionToLeg(p, true);
                }
                unResolvableDirections.push(direction.id)

                //We have several legs that can handle this direction.
                //Let's be smart and try to auto-resolve according to ho the shape of the legs.
                // let stdFiltered = projections.filter(p => legBuilders[p.legIndex].computeHeadingStd(p.direction.point) > PATH_BREAK_MIN_STD);   //Here we detect if there's a direction change on a given projection.
                // if(stdFiltered.length === 1) {
                //     legBuilders[stdFiltered[0].legIndex].addDirectionProjectionToLeg(projections[0], false);
                //     smartResolvedDirections.push(stdFiltered[0].direction.id)
                // } else {
                //     for(let p of projections) {
                //         legBuilders[p.legIndex].addDirectionProjectionToLeg(p, true);
                //     }
                //     unResolvableDirections.push(direction.id)
                // }
            }
        }

        return {
            fr: fullyResolvableDirections,
            oot: unMatchableDirections,
            sr: smartResolvedDirections,
            ur: unResolvableDirections
        }
    }



    /**
     * General idea of this : poorly sequenced stops should not break the system.
     * @param stops
     * @param legBuilders
     */
    positionStops(stops: IStop[], legBuilders: LegBuilder[]) {
        let exceptions:IStop[] = [];    //Stops that are too far away from the track.
        for(let i = 0; i < stops.length; i++) {
            let results = legBuilders
                .map(e => e.projectStop(stops[i]))
                .sort((r1, r2) => r1.gap - r2.gap)
                .filter(e => e.gap < MAX_STOP_TRACK_GAP);
            if(results.length === 0) {
                exceptions.push(stops[i]);  //Stops is too far away from track.
            } else if(results.length === 1) {
                legBuilders[results[0].legIndex].addStopProjectionToLeg(results[0], false);
            } else {
                for(let result of results) {
                    legBuilders[result.legIndex].addStopProjectionToLeg(result, true);
                }
            }
        }

        //Check if the stops that are certain are correctly sorted :
        if(this.checkDistanceSequenceMatch(legBuilders)) {
            let keepGoing = true;
            do {
                let legsCopy = legBuilders.map(e => e.deepCopy());
                if(this.resolveUncertainty(legsCopy, stops) && this.checkDistanceSequenceMatch(legsCopy)) {
                    //A new uncertainty was resolved AND the distances check is still valid. We can go one.
                    legBuilders = legsCopy;
                } else {
                    keepGoing = false;  //There is nothing more to do or this is failing.
                }
            } while(keepGoing);
        }
    }


    /**
     * sureSPList are sorted by stop sequence, which is equal to sort them by complete distance since distanceSequenceMatch should always have been
     * performed prior this function call. Sort is DESC complete distance because we always need to find the one right before the one we are working with.
     */
    resolveUncertainty(legBuilders: LegBuilder[], stops:IStop[]) {
        let unCertaintySolved = false;      //Will say if a certainty has been resolved or not
        //1. Find uncertain stops
        let unSureSPList = legBuilders.map(e => e.getUnSureStopsProjections()).flat();
        let sureSPList = legBuilders
            .map(e => e.getSureStopsProjections())
            .flat()
            .sort((a,b) => (b.completeDistance -a.completeDistance));


        for(let unSureSP of unSureSPList) {
            let sureBeforeList = sureSPList
                .filter(e => e.completeDistance < unSureSP.completeDistance);

            //Remove it if this one is not matching.
            if(sureBeforeList.length && sureBeforeList[0].stop.sequence > unSureSP.stop.sequence) {
                legBuilders[unSureSP.legIndex].removeStopProjectionFromLeg(unSureSP.stop.id); //certainsPrior[0].stop has the highest sequence.
            }
        }

        //2. for each stop, check if it appears only in one leg, if this is the case, set it as certain.
        for(let stop of stops) {
            let pList = legBuilders.map(e => e.getStopProjection(stop.id)).filter(e => e !== null);
            if(pList.length === 1) {
                if(pList[0] !== null) {
                    if(pList[0].unsure) {
                        unCertaintySolved = true; // We found a projection that was unsure which is now sure.
                    }
                    legBuilders[pList[0].projection.legIndex].updateStopCertainty(pList[0].projection.stop.id, false);
                }
            } //Certainty was solved !
        }

        return unCertaintySolved;
    }

    checkDistanceSequenceMatch(legBuilders: LegBuilder[]) {
        let sortedByDistance = legBuilders
            .map(e => e.getSureStopsProjections())
            .flat()
            .sort((a,b) => a.completeDistance - b.completeDistance);

        for(let i = 1; i < sortedByDistance.length; i++) {
            if(sortedByDistance[i-1].stop.sequence >= sortedByDistance[i].stop.sequence){
                console.log("Sure stops are not correctly sorted, we cannot resolve uncertainties at all.");
                return false;
            }
        }
        return true;
    }

    usingResolvedStopUncertainties(stopUncertaintyResolutions:IUncertaintyResolution[]) : this {
        this.preComputedStopUncertaintyResolutions = stopUncertaintyResolutions;
        return this;
    }

    usingResolvedDirectionUncertainties(directionUncertaintyResolutions:IUncertaintyResolution[]) : this {
        this.precomputedDirectionUncertaintyResolutions = directionUncertaintyResolutions;
        return this;
    }


    public static solveStopUnCertainty(byteCode: IBytecode, stop_id: string, legIndex: number) {

        let refLat = 0;
        let refLon = 0;
        for(let j = 0; j < byteCode.legs.length; j++) {
            let leg = byteCode.legs[j];
            console.log(leg.containedStops.find(e => e.stopId === stop_id));
            if(leg.containedStops.find(e => e.stopId === stop_id) !== undefined) {
                console.log("Found stop with id " + stop_id + " on leg " + j);
                for(let i = 0; i < leg.containedStops.length; i++) {
                    let cs = leg.containedStops[i];
                    if(cs.stopId === stop_id) {
                        if(legIndex === j) {
                            byteCode.legs[j].containedStops[i].unSure = false;
                            refLat = byteCode.legs[j].containedStops[i].refLat;
                            refLon = byteCode.legs[j].containedStops[i].refLon;
                        } else {
                            byteCode.legs[j].containedStops.splice(i, 1);
                        }
                    }
                }
            }
        }
        byteCode.stopUncertaintyResolutions.push({
            element_id: stop_id,
            legIndex: legIndex,
            refLat: refLat,
            refLon: refLon
        })
        return byteCode;
    }
    public static solveDirectionUncertainty(byteCode: IBytecode, direction_id: string, legIndex: number) {
        let refLat = 0;
        let refLon = 0;
        for(let j = 0; j < byteCode.legs.length; j++) {
            let leg = byteCode.legs[j];
            if(leg.containedDirections.find(e => e.directionId === direction_id) !== undefined) {
                for(let i = 0; i < leg.containedDirections.length; i++) {
                    let cs = leg.containedDirections[i];
                    if(cs.directionId === direction_id) {
                        if(legIndex === j) {
                            byteCode.legs[j].containedDirections[i].unSure = false;
                            refLat = byteCode.legs[j].containedDirections[i].refLat;
                            refLon = byteCode.legs[j].containedDirections[i].refLon;
                        } else {
                            byteCode.legs[j].containedDirections.splice(i, 1);
                        }
                    }
                }
            }
        }

        byteCode.directionUncertaintyResolutions.push({
            element_id: direction_id,
            legIndex: legIndex,
            refLat: refLat,
            refLon: refLon
        })

        return byteCode;
    }

}